import * as React from "react";
import { Sun, Moon } from "lucide-react";

/**
 * SolarShift-style dark mode toggle. Persists user choice in
 * localStorage under "caring:theme". Applies `.dark` on <html>.
 * Respects system preference on first visit if no choice stored.
 *
 * Usage:
 *   <ThemeToggle />           // default 32px icon button
 *   <ThemeToggle size="lg" /> // larger for public pages (44px+)
 */
const STORAGE_KEY = "caring:theme";

function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
    // Fall back to system preference
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch (_) { /* storage unavailable */ }
  return "light";
}

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function ThemeToggle({ size = "default", className = "" }) {
  const [theme, setTheme] = React.useState(getInitialTheme);

  React.useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (_) {}
  }, [theme]);

  // Also apply on first mount in case SSR mismatched
  React.useEffect(() => { applyTheme(getInitialTheme()); }, []);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const sizing = size === "lg"
    ? "h-11 w-11 rounded-xl"
    : "h-9 w-9 rounded-lg";

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={theme === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro"}
      title={theme === "dark" ? "Modo claro" : "Modo escuro"}
      className={`${sizing} inline-flex items-center justify-center border border-border bg-card text-foreground hover:bg-accent hover:border-primary/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
    >
      {theme === "dark" ? (
        <Sun className="w-4 h-4" strokeWidth={1.8} data-testid="theme-toggle-icon-sun" />
      ) : (
        <Moon className="w-4 h-4" strokeWidth={1.8} data-testid="theme-toggle-icon-moon" />
      )}
    </button>
  );
}

export default ThemeToggle;
